import {MuNamedWidgets, MuWidget} from "mu-widget/lib/MuWidget";
import {Triggers} from "mu-js-utils/lib/Triggers";
import {TNames} from "../../TNames";
import {openInPanel} from "../../appPanel";
import {BasePanelWidget} from "../../BasePanelWidget";
import {DayStats, foodStatComponents, JournalActivity, MyJournal} from "../../types";
import {frontRouter} from "../../FrontRouter";
import {addFlash, elementToAiText, formatEnergy, formatWeight, isToday} from "../../utils";
import {BarMeterData} from "../BarMeter";
import {t} from "../../translate";
import {JournalActivityUtils} from "../../JournalActivityUtils";
import {appTL} from "../../gen/tl";

function calculateAge(dtBirth: string): number {
    const birth = new Date(dtBirth);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
    }
    return age;
}

export class UiNutriInfoPanel extends MuWidget {
    beforeIndex() {
        this.container.classList.add("nutri-info-panel");
        this.muAppendContent(`
            <div mu="meter:BarMeter"></div>
        `);
    }

    protected dayStats: DayStats|null = null;
    protected activities: JournalActivity[] = [];

    afterIndex(): void | Promise<any> {
        Triggers.addHandler(TNames.dayLoaded, (ev, data: MyJournal) => {
            this.dayStats = data.dayStats;
            this.activities = data.activities;
            if (data.dayStats.recommendedValues === null) {
                this.muNamedWidget.meter.muBindData({
                    min: 0,
                    max: 1,
                    value: 1,
                    points: [],
                    color: '#ffffff00',
                    label: formatEnergy(data.dayStats.currentValues.energy, false, true)
                } as BarMeterData);
            } else {
                this.muNamedWidget.meter.muBindData({
                    min: 0,
                    max: data.dayStats.recommendedValues.energy,
                    value: data.dayStats.currentValues.energy,
                    points: [
                        { value: data.dayStats.recommendedValues.energy, color: '#FF0000' },
                        ...(isToday(data.day)
                            ? [ { value: data.dayStats.dayTimeRecommended, color: '#009900' } ]
                            : [ ]),
                    ],
                    color: [
                        { color: "#3333ff", value: 0 },
                        { color: "#009900", value: data.dayStats.dayTimeRecommended },
                        { color: "#FF5500", value: data.dayStats.recommendedValues.energy * 1.05 },
                        { color: "#FF0000", value: data.dayStats.recommendedValues.energy * 1.5 },
                    ],
                    label: formatEnergy(data.dayStats.currentValues.energy, false, true)
                        + ' / ' + formatEnergy(data.dayStats.dayTimeRecommended, false, true)
                        + ' / ' + formatEnergy(data.dayStats.recommendedValues.energy, true, true),

                } as BarMeterData);
            }
        });
    }

    async container_click() {
        (await openInPanel('UiNutriInfoDetails')).muBindData({
            ...this.dayStats,
            activities: this.activities,
        });
    }
}

export class UiNutriInfoData extends MuWidget {
    beforeIndex() {
        this.muAppendContent(
            `
            <dl class="num-pairs" mu="#showDetailData:@visible">
                <dt>Aktuální hodnota: </dt><dd mu="#currentEnergy|formatEnergy"></dd>
                <dt mu="#isToday:@visible">Cíl pro teď: <span mu="#dayTimePer"></span></dt><dd mu="#isToday:@visible;dayTimeRecommended|formatEnergy"></dd>
                <dt>Cíl pro celý den: <span mu="#totalPer"></span></dt><dd mu="#recommendedEnergy|formatEnergy"></dd>
                <dt>
                    Energie spotřebovaná aktivitami:
                    <!-- <div class="num-pairs__details">
                        <span mu="#activityLevel|prepend('icon-size--24 icon--')"
                        Aktivní den
                    </div> -->
                </dt><dd mu="#energyFromActivities|formatEnergy"></dd>
                <dt>Bazální energie: </dt><dd mu="#basalEnergy|formatEnergy"></dd>
                <dt>Voda: </dt><dd><span mu="#water"></span><spam mu="bCopy">ml</spam></dd>
            </dl>

            <h3>Podle složek potravy</h3>
            <table class="nutri-info--details__table">
                <thead>
                    <tr>
                        <th></th>
                        <th class="text-left" mu="#showDetailData:@visible">Cíl</th>
                        <th class="text-left">Aktuálně</th>
                        <th class="text-left" mu="#showDetailData:@visible">Podíl</th>
                    </tr>
                </thead>
                <tbody mu="#lines:@foreach">
                    <tr mu=":.@line">
                        <th class="nutri-info--details__cell-label" mu="#label"></th>
                        <td class="nutri-info--details__cell-recommended" mu="#recommended;showDetailData:@visible"></td>
                        <td class="nutri-info--details__cell-current" mu="#current"></td>
                        <td class="nutri-info--details__cell-completed" mu="#completed;showDetailData:@visible"></td>
                    </tr>
                </tbody>
            </table>
            <p mu="#showDetailData|not:@visible">
                Pro zobrazení doporučených hodnot nastav/doplň své údaje.
            </p>
            <div>
                <a mu="bClientData">Zadat moje údaje</a>
            </div>

            <div class="ai-only" mu="#hasFoodLines:@visible">
                <h3>${t('nutriInfoFoodTitle')}</h3>
                <table>
                    <tbody mu="#foodLines:@foreach">
                        <tr mu=":.@foodLine">
                            <td mu="#time"></td>
                            <td mu="#name"></td>
                            <td mu="#value"></td>
                            <td mu="#note"></td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="ai-only" mu="#hasActivityLines:@visible">
                <h3>${t('nutriInfoActivityTitle')}</h3>
                <table>
                    <tbody mu="#activityLines:@foreach">
                        <tr mu=":.@activityLine">
                            <td mu="#time"></td>
                            <td mu="#name"></td>
                            <td mu="#value"></td>
                            <td mu="#note"></td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="ai-only" mu="#hasMeasureLines:@visible">
                <h3>${t('nutriInfoMeasureTitle')}</h3>
                <table>
                    <tbody mu="#measureLines:@foreach">
                        <tr mu=":.@measureLine">
                            <td mu="#time"></td>
                            <td mu="#name"></td>
                            <td mu="#value"></td>
                            <td mu="#note"></td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="ai-only" mu="#hasProfileData:@visible">
                <h3>${t('nutriInfoProfileTitle')}</h3>
                <dl>
                    <dt>${t('clientDataLabelProfile')}: </dt><dd mu="#profileGoal"></dd>
                    <dt>${t('clientDataLabelHeight')}: </dt><dd mu="#profileHeight"></dd>
                    <dt>${t('clientDataLabelMass')}: </dt><dd mu="#profileMass"></dd>
                    <dt>${t('nutriInfoLabelAge')}: </dt><dd mu="#profileAge"></dd>
                    <dt>${t('clientDataLabelGender')}: </dt><dd mu="#profileGender"></dd>
                </dl>
            </div>`
        );
    }

    protected profileData: {
        profileGoal: string,
        profileHeight: string,
        profileMass: string,
        profileAge: string,
        profileGender: string,
    } | null = null;
    protected lastSrcData: (DayStats&any) | null = null;

    afterIndex(): void | Promise<any> {
        frontRouter.prepareAnchor(this.ui.bClientData, 'clientData', {});
        this.loadProfileData();
        Triggers.addHandler(TNames.clientDataChanged, () => this.loadProfileData());
    }

    protected async loadProfileData() {
        const {data, numberings} = await appTL.getClientDataAndNumberings();

        this.profileData = {
            profileGoal: numberings.profile.find(item => item.value === data.profile)?.text ?? '',
            profileHeight: data.height ? `${data.height} cm` : '',
            profileMass: data.mass ? `${data.mass} kg` : '',
            profileAge: data.dtBirth ? `${calculateAge(data.dtBirth)} let` : '',
            profileGender: numberings.gender.find(item => item.value === data.gender)?.text ?? '',
        };

        if (this.lastSrcData) {
            this.muBindData(this.lastSrcData);
        }
    }

    async bCopy_click() {
        try {
            await navigator.clipboard.writeText(elementToAiText(this.container));
            addFlash(t('nutriInfoCopied'), 'ok');
        } catch {
            addFlash(t('nutriInfoCopyFailed'), 'error');
        }
    }

    muBindData(srcData: DayStats&any) {
        this.lastSrcData = srcData;

        const showDetailData = !!srcData.recommendedValues;
        srcData.lines = [];
        for (const field of foodStatComponents) {
            const row: any = {
                label: field.label,
                showDetailData,
            };
            row.current = srcData.currentValues[field.key];
            row.recommended = srcData.recommendedValues?.[field.key];

            row.completed = row.recommended
                ? Math.round((row.current * 100) / row.recommended).toString() + "%"
                : '-';

            if (field.unit === '#energy') {
                row.current = formatEnergy(row.current);
                row.recommended = formatEnergy(row.recommended);
            } else {
                row.current = formatWeight(row.current);
                row.recommended = formatWeight(row.recommended);
            }

            srcData.lines.push(row);
        }

        const activities: JournalActivity[] = srcData.activities ?? [];
        const isMeasure = (a: JournalActivity) => a.activityOptions?.layout === 'measure';
        const toLine = (a: JournalActivity) => ({
            time: JournalActivityUtils.formatTime(a),
            name: JournalActivityUtils.formatActivity(a),
            value: a.values?.length
                ? a.values.map(v => `${v.activityVariantName}: ${v.value}`).join(', ')
                : JournalActivityUtils.formatValue(a),
            note: a.note ?? '',
        });
        const foodLines = activities.filter(a => a.activityCode === 'food' && !isMeasure(a)).map(toLine);
        const activityLines = activities.filter(a => a.activityCode === 'activity' && !isMeasure(a)).map(toLine);
        const measureLines = activities.filter(isMeasure).map(toLine);

        super.muBindData({
            ...srcData,
            showDetailData,
            currentEnergy: srcData.currentValues.energy,
            recommendedEnergy: srcData.recommendedValues?.energy,
            dayTimePer: srcData.dayTimeRecommended
                ? '(' + Math.round((srcData.currentValues.energy / srcData.dayTimeRecommended) * 100) + '%)'
                : '',
            totalPer: srcData.recommendedValues?.energy
                ? '(' + Math.round((srcData.currentValues.energy / srcData.recommendedValues?.energy) * 100) + '%)'
                : '',
            foodLines,
            activityLines,
            measureLines,
            hasFoodLines: foodLines.length > 0,
            hasActivityLines: activityLines.length > 0,
            hasMeasureLines: measureLines.length > 0,
            ...this.profileData,
            hasProfileData: !!this.profileData,
        });
    }
}

export class UiNutriInfoDetails extends BasePanelWidget {
    // @ts-ignore
    muNamedWidget: MuNamedWidgets<{ content: UiNutriInfoData }>;

    beforeIndex() {
        this.muAppendContent(`<div mu="content:UiNutriInfoData"></div>`);
    }

    afterIndex(): void | Promise<any> {
        Triggers.addHandler(TNames.dayLoaded, (_ev, data) => {
            this.muBindData({
                ...data.dayStats,
                isToday: isToday(data.day),
                activities: data.activities,
            });
        });
        this.setTitle('Výživové hodnoty za den');
    }

    muBindData(srcData: DayStats&any) {
        this.muNamedWidget.content.muBindData(srcData);
    }
}